<script>
  import alertify from "alertifyjs";
  import { ICONS } from "../js/icons";
  import backend from "../server";
  import CampaignFilters from "../lib/components/modals/freebets/anakatech/CampaignFilters.svelte";
  import CreateFreeSpinCampaignModal from "../lib/components/modals/freebets/anakatech/CreateFreeSpinCampaignModal.svelte";
  import FreeSpinCampaignTable from "../lib/components/modals/freebets/anakatech/FreeSpinCampaignTable.svelte";
  import FreeSpinPlayersModal from "../lib/components/modals/freebets/anakatech/FreeSpinPlayersModal.svelte";
  import { loadAnakatechClients } from "../lib/components/modals/freebets/anakatech/utils/anakatechCatalogs.js";
  import { CAMPAIGN_STATUS } from "../lib/utils/statusEngine.js";

  export let icons = ICONS;

  let filters = {
    client: "",
    clientId: "",
    clientName: "",
    campaignName: "",
    statuses: [CAMPAIGN_STATUS.ACTIVE, CAMPAIGN_STATUS.SCHEDULED],
  };
  let freeSpinListRef;
  let playerTarget = null;
  let playerTargetClient = null;
  let showPlayerManagement = false;
  let isUpdatingRecord = false;
  let isNew = false;
  let isSaving = false;
  let modalVersion = 0;

  const api = {
    listCampaigns: backend.freespin.listCampaignByProviderIgp,
    deleteFreeSpin: backend.freespin.deleteFreeSpinAnakatech,
  };

  function openCreate() {
    modalVersion++;
    isNew = true;
  }

  async function saveFreeSpin(event) {
    const { clientCode, currency, request } = event.detail;
    try {
      isSaving = true;
      await backend.freespin.saveAnakatech(clientCode, currency, request);
      await freeSpinListRef?.refresh?.();
      alertify.success("Free Spin creado correctamente");
      isNew = false;
    } catch (error) {
      console.error("Error creando Free Spin de Anakatech:", error);
      alertify.error("No se pudo crear el Free Spin");
    } finally {
      isSaving = false;
    }
  }

  function recordClientCode(record) {
    const code =
      record?.clientCode ??
      record?.client?.clientCode ??
      record?.client?.code ??
      (["string", "number"].includes(typeof record?.client) ? record.client : "");
    return code === null || code === undefined ? "" : String(code).trim();
  }

  async function openPlayerManagement(record) {
    const campaignClientCode = recordClientCode(record);
    let resolvedClient = null;
    try {
      const { clients } = await loadAnakatechClients();
      resolvedClient = clients.find(
        (client) => String(client?.code) === campaignClientCode,
      );
    } catch (error) {
      console.error("Error resolviendo el cliente de la campaña:", error);
    }
    const clientId =
      record?.client?.id ?? record?.client?.clientId ?? resolvedClient?.id ?? "";
    if (!clientId) {
      alertify.error("No se pudo resolver el cliente de la campaña");
      return;
    }
    playerTarget = record;
    playerTargetClient = resolvedClient || {
      id: clientId,
      code: campaignClientCode,
      currency: record?.currency,
    };
    showPlayerManagement = true;
  }

  function targetClientId() {
    return playerTarget?.client?.id ??
      playerTarget?.client?.clientId ??
      playerTargetClient?.id ??
      "";
  }

  function targetCurrency() {
    const currency = playerTargetClient?.currency || playerTarget?.currency || "";
    return currency === "-" ? "" : currency;
  }

  function firstNonBlank(...values) {
    const match = values.find(
      (value) =>
        value !== null &&
        value !== undefined &&
        String(value).trim().length > 0,
    );
    return match === null || match === undefined ? "" : String(match).trim();
  }

  async function addSelectedPlayers(event) {
    const players = event.detail?.players || [];
    if (isUpdatingRecord || !playerTarget || !players.length) return;
    const freeRoundId = String(
      playerTarget?.freeRoundId ??
      playerTarget?.campaignId ??
      playerTarget?.code ??
      playerTarget?.rowId ??
      "",
    ).trim();
    if (!freeRoundId) {
      alertify.error("El Free Spin no tiene freeRoundId");
      return;
    }
    const playerList = players.map((player) => {
      const id = firstNonBlank(player.id, player.value);
      const username = firstNonBlank(
        player.username,
        player.username_origin,
        player.label,
        id,
      );
      const name = firstNonBlank(
        player.firstname,
        player.label,
        player.realName,
        player.name,
        player.username_origin,
        player.username,
        id,
      );
      const numericId = Number(id);
      return {
        id: Number.isSafeInteger(numericId) ? numericId : null,
        username,
        name,
      };
    });
    isUpdatingRecord = true;
    try {
      const response = await backend.freespin.addPlayersToFreeRoundsAnakatech(
        freeRoundId,
        recordClientCode(playerTarget),
        { playerList },
      );
      const providerRows = Array.isArray(response?.data) ? response.data : [];
      const acceptedIds = new Set(
        providerRows
          .map((row) => firstNonBlank(row?.playerId))
          .filter(Boolean),
      );
      const currentPlayers = Array.isArray(playerTarget?.playerList)
        ? playerTarget.playerList
        : [];
      const currentIds = new Set(
        currentPlayers
          .map((player) => firstNonBlank(player?.playerId, player?.id))
          .filter(Boolean),
      );
      const addedPlayers = playerList
        .filter(
          (player) => {
            const providerPlayerId = firstNonBlank(player.id, player.username);
            return acceptedIds.has(providerPlayerId) && !currentIds.has(providerPlayerId);
          },
        )
        .map((player) => {
          const providerPlayerId = firstNonBlank(player.id, player.username);
          const providerRow = providerRows.find(
            (row) => firstNonBlank(row?.playerId) === providerPlayerId,
          );
          return {
            playerId: providerPlayerId,
            playerName: player.name || player.username || providerPlayerId,
            playerStatus: 1,
            baseBonusCode: firstNonBlank(
              providerRow?.freeRoundId,
              `${freeRoundId}${providerPlayerId}`,
            ),
            assignationDate: new Date().toISOString(),
          };
        });
      playerTarget = {
        ...playerTarget,
        playerList: [...currentPlayers, ...addedPlayers],
      };
      await freeSpinListRef?.refresh?.();
      alertify.success("Jugadores añadidos correctamente");
    } catch (error) {
      console.error("Error añadiendo jugadores en Anakatech:", error);
      alertify.error("No se pudieron añadir los jugadores");
    } finally {
      isUpdatingRecord = false;
    }
  }

  async function deleteFreeSpin(freeSpin) {
    if (!confirm(`¿Eliminar la campaña local ${freeSpin.displayName}?`)) return;
    try {
      await api.deleteFreeSpin(freeSpin.campaignId);
      await freeSpinListRef?.refresh?.();
      alertify.success("Free Spin eliminado correctamente");
    } catch (error) {
      console.error("Error eliminando Free Spin:", error);
      alertify.error("No se pudo eliminar el Free Spin");
    }
  }

  async function removePlayer(event) {
    const player = event.detail?.player;
    const campaignId = String(playerTarget?.campaignId ?? "").trim();
    const playerId = firstNonBlank(player?.playerId, player?.id);
    if (!campaignId || !playerId || isUpdatingRecord) return;
    if (!confirm(`¿Eliminar al jugador ${player?.playerName || playerId} de la campaña?`)) return;

    isUpdatingRecord = true;
    try {
      await backend.freespin.deletePlayerFreeSpinAnakatech(campaignId, playerId);
      playerTarget = {
        ...playerTarget,
        playerList: (playerTarget?.playerList || []).filter(
          (item) => String(item?.playerId ?? item?.id) !== String(playerId),
        ),
      };
      await freeSpinListRef?.refresh?.();
      alertify.success("Jugador eliminado correctamente");
    } catch (error) {
      console.error("Error eliminando jugador de Anakatech:", error);
      alertify.error("No se pudo eliminar el jugador");
    } finally {
      isUpdatingRecord = false;
    }
  }

</script>

<CampaignFilters
  bind:filters
  {icons}
  on:create={openCreate}
/>

<FreeSpinCampaignTable
  bind:this={freeSpinListRef}
  {api}
  {filters}
  {icons}
  on:addplayers={(event) => openPlayerManagement(event.detail)}
  on:delete={(event) => deleteFreeSpin(event.detail)}
/>

{#if showPlayerManagement && playerTarget}
  <FreeSpinPlayersModal
    campaign={playerTarget}
    clientId={targetClientId()}
    currency={targetCurrency()}
    assignedPlayers={playerTarget?.playerList || []}
    busy={isUpdatingRecord}
    on:confirm={addSelectedPlayers}
    on:remove={removePlayer}
    on:close={() => (showPlayerManagement = false)}
  />
{/if}

{#if isNew}
  {#key modalVersion}
    <CreateFreeSpinCampaignModal
      bind:isNew
      clientCode={filters.client}
      clientId={filters.clientId}
      saving={isSaving}
      on:close={() => (isNew = false)}
      on:save={saveFreeSpin}
    />
  {/key}
{/if}

<style>
  :global(.btn-pink) { background-color: #2a1a28; color: white; }
  :global(.btn-pink:hover) { background-color: #ff0090; color: white; }
</style>
